import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Stack } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { observer } from 'mobx-react'
import React, { FC } from 'react'
import { useForm, UseFormWatch } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import z from 'zod'
import { getImageSrc } from '../../../../../helpers/getImageSrc'
import { placeNames } from '../../../../../helpers/globalVariables'
import { useFileLoader } from '../../../../../hooks/useFileLoader'
import { useInvalidateQueries } from '../../../../../hooks/useInvalidateQueries'
import { useTerritoryFields } from '../../../../../hooks/useTerritoryFields'
import { isDealerWE } from '../../../../../services/AccountService'
import { orderClarifyData } from '../../../../../services/ApiService'
import { notifySuccess } from '../../../../../services/ToastifyService/ToastifyService'
import { getComp } from '../../../../../utils/utils/DI'
import { checkUploadedImages } from '../../../../SingleRequestPage/components/SingleRequestDrawer/content/DrawerContentInstallation'
import FormAutocomplete from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormAutocomplete'
import FormInput from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormInput'
import FormSelect from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormSelect'
import { getCollections } from '../../../../SingleRequestPage/components/SingleRequestDrawer/helpers/getCollections'
import { dotationImageSx } from '../../../../SingleRequestPage/components/SingleRequestDrawer/styles/styles'
import { RequestType } from '../../../../SingleRequestPage/types/Request.type'
import { REPORT_KEYS } from '../../../../SingleRequestReport/consts/request-report.keys'
import { newRequestDrawerStore } from '../../../stores/NewRequestDrawerStore'
import NewPhotosUpload from '../../InstalationRequestDrawer/components/NewPhotosUpload'

type EditFullCustomerInfoProps = {
  request: RequestType
  onClose: () => void
}

export const EditFullCustomerInfo: FC<EditFullCustomerInfoProps> = ({
  request,
  onClose,
}) => {
  const intl = useIntl()
  const invalidate = useInvalidateQueries()
  const { getInstallationReasonName } = getComp('InstallationReasonStore')
  const reasonName = getInstallationReasonName(request.reasonId)
  const { files, handleSetFiles, deleteFile, photoPermission } =
    useFileLoader('clarify')

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<ChangeInfoFormType>({
    defaultValues: getDefaultInfoValues(request, reasonName),
    mode: 'onChange',
    resolver: zodResolver(changeInfoSchema()),
  })

  const {
    onTerritoryChange,
    onDistrictChange,
    fullDotationRegions,
    reasonsItems,
    categoriesItems,
    onCategoriesChange,
    districtOptions,
    localitiesOptions,
    locality,
    territory,
  } = useTerritoryFields(watch, setValue, request.reasonId)

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['update-clarifying-request-by-dealer'],
    mutationFn: (params: Record<string, any>) => orderClarifyData(params),
    onSuccess: (data) => {
      const result = checkUploadedImages(data)
      if (result) {
        newRequestDrawerStore.updateRequest(data)
        invalidate([
          'requests-table',
          'get-request-images',
          REPORT_KEYS.GET_REPORT_TABLE,
        ])
        notifySuccess('Заявка обновлена')
        onClose()
      }
    },
  })

  const onSave = async (data: ChangeInfoFormType) => {
    let params: Record<string, any> = {
      orderId: request.id,
      reasonId: data.reasonId?.key ?? request.reasonId,
      categories: data.categories
        .map((c, i) => (!!c ? i : -1))
        .filter((i) => i >= 0),
      ...files?.map((file) => file.file),
    }

    if (!isDealerWE()) {
      params = {
        ...data,
        orderId: request.id,
        reasonId: data.reasonId?.key ?? request.reasonId,
        phone: data.phone ? normalizePhoneNumber(data.phone) : data.phone,
        categories: data.categories
          .map((c, i) => (!!c ? i : -1))
          .filter((i) => i >= 0),
        ...files?.map((file) => file.file),
      }
    }

    await mutateAsync(params)
  }

  const disableSaveBtn =
    !!Object.keys(errors).length ||
    (!isAtLeastOneChanged(getDefaultInfoValues(request, reasonName), watch) &&
      !files?.length)

  return (
    <Stack
      height={'100%'}
      justifyContent={'space-between'}
      component={'form'}
      onSubmit={handleSubmit(onSave)}
    >
      <Stack spacing={'16px'}>
        <FormSelect
          control={control}
          fullWidth
          name='reasonId'
          label='Причина обращения'
          items={reasonsItems}
          placeholder=''
          withLabel
          width={'350px'}
        />
        {!isDealerWE() && (
          <>
            <FormInput
              control={control}
              fullWidth
              name='surname'
              label='Фамилия'
              capitalizeFirstLetter
              placeholder=''
            />
            <FormInput
              control={control}
              fullWidth
              name='name'
              label='Имя'
              capitalizeFirstLetter
              placeholder=''
            />
            <FormInput
              control={control}
              fullWidth
              label='Отчество'
              name='patronymic'
              placeholder=''
              capitalizeFirstLetter
            />
            <FormInput
              control={control}
              fullWidth
              name='phone'
              label='Телефон'
              placeholder=''
              mask={'+7 (999)-999-99-99'}
            />
            <FormSelect
              control={control}
              fullWidth
              name='territory'
              items={getComp('TerritoryStore').collection as string[]}
              label='Регион'
              placeholder=''
              error={!getComp('TerritoryStore').collection.includes(territory)}
              //   @ts-ignore
              onChange={onTerritoryChange}
              withLabel
              width={'350px'}
            />
            <FormAutocomplete
              control={control}
              fullWidth
              name='district'
              options={districtOptions}
              placeholder=''
              freeSolo
              withUnknownValueError
              label='Округ'
              onChange={onDistrictChange}
            />
            <Box sx={{ position: 'relative' }}>
              <FormAutocomplete
                control={control}
                fullWidth
                name='locality'
                options={localitiesOptions}
                label='Населённый пункт'
                placeholder=''
                fullDotationRegions={fullDotationRegions}
                freeSolo
                withUnknownValueError
              />
              {fullDotationRegions?.includes(locality ?? '') && (
                <Box sx={{ ...dotationImageSx, top: '23%' }}>
                  <img
                    src={getImageSrc('full-subsidy')}
                    alt='full subsidy icon'
                  />
                </Box>
              )}
            </Box>
            <FormAutocomplete
              control={control}
              fullWidth
              name='addressType'
              label='Топоним'
              options={placeNames}
              withoutTyping
              placeholder=''
              freeSolo
            />
            <FormInput
              control={control}
              fullWidth
              name='address'
              label='Наименование топонима'
              placeholder=''
            />
            <Stack direction={'row'} spacing={'10px'} maxWidth={'100%'}>
              <FormInput
                control={control}
                fullWidth
                name='house'
                label='Дом'
                placeholder=''
                onlyNumbers
              />
              <FormInput
                control={control}
                fullWidth
                name='building'
                label='Корпус'
                placeholder=''
                onlyNumbersAndLetters
              />
              <FormInput
                control={control}
                fullWidth
                name='apartment'
                label='Квартира'
                placeholder=''
                onlyNumbers
              />
            </Stack>
            <FormInput
              control={control}
              fullWidth
              name='preferentialCategory'
              label='Льготная категория'
              placeholder=''
            />
          </>
        )}
        <FormSelect
          control={control}
          fullWidth
          items={categoriesItems}
          name='categories'
          label='Льготные категории'
          placeholder=''
          multiple
          onChange={onCategoriesChange}
          disableDefaultOnChange
          withLabel
          width={'350px'}
        />
        <NewPhotosUpload
          request={request}
          photoSettings={photoPermission}
          title={intl.formatMessage(
            {
              id: `Загрузите от {minimumLimit} до {maximumLimit} фотографий`,
              defaultMessage: `Загрузите от {minimumLimit} до {maximumLimit} фотографий`,
            },
            {
              minimumLimit: photoPermission?.minLimitPerLoad,
              maximumLimit: photoPermission?.maxLimitPerLoad,
            },
          )}
          collection={files ?? []}
          setFile={handleSetFiles}
          deleteFile={deleteFile}
        />
      </Stack>
      <Stack direction={'row'} gap={'8px'} mt={'24px'}>
        <Button
          color={'primary'}
          variant={'contained'}
          fullWidth
          disabled={disableSaveBtn}
          type='submit'
          loading={isPending}
        >
          <FormattedMessage id={'применить изменения'} />
        </Button>
        <Button
          color={'primary'}
          variant={'outlined'}
          onClick={onClose}
          fullWidth
        >
          <FormattedMessage id={'Отмена'} />
        </Button>
      </Stack>
    </Stack>
  )
}

export const baseOptionSchema = z.object({
  key: z.number(),
  value: z.string(),
  iconSrc: z.string().optional(),
})

export const changeInfoSchema = () =>
  z
    .object({
      reasonId: baseOptionSchema.nullable(),
      name: z.string().nullable(),
      surname: z.string().nullable(),
      patronymic: z.string().nullable(),
      phone: z.string().nullable(),
      territory: z.string().nullable(),
      locality: z
        .string()
        .nullable()
        .refine((val) => !!val),
      addressType: z.string().nullable(),
      address: z.string().nullable(),
      house: z.string().nullable(),
      building: z.string().nullable(),
      apartment: z.string().nullable(),
      district: z
        .string()
        .nullable()
        .refine((val) => !!val),
      preferentialCategory: z.string().nullable(),
      categories: z.array(z.boolean()),
    })
    .refine(
      (data) => {
        const { districtOptions } = getCollections(
          data.territory,
          data.district,
          data.locality,
          -1,
        )
        return districtOptions.includes(data.district)
      },
      {
        message: 'invalid',
        path: ['district'],
      },
    )
    .refine(
      (data) => {
        const { localitiesOptions } = getCollections(
          data.territory,
          data.district,
          data.locality,
          -1,
        )
        return localitiesOptions.includes(data.locality)
      },
      {
        message: 'invalid',
        path: ['locality'],
      },
    )

export const getDefaultInfoValues = (
  request: RequestType,
  reasonName: any,
): ChangeInfoFormType => {
  return {
    reasonId: {
      key: request.reasonId ?? 1,
      value: reasonName ?? 'Не определена',
    },
    address: request.address,
    addressType: request.addressType,
    apartment: request.apartment,
    building: request.building,
    district: request.district,
    house: request.house,
    locality: request.locality,
    territory: request.territory,
    name: request.name,
    patronymic: request.patronymic,
    phone: request.phone,
    surname: request.surname,
    preferentialCategory: request.preferentialCategory,
    categories: request.categories,
  }
}

export type ChangeInfoFormType = z.infer<ReturnType<typeof changeInfoSchema>>

function normalizePhoneNumber(phoneNumber: string) {
  return phoneNumber.replace(/\D/g, '')
}

const isAtLeastOneChanged = (
  defaultValues: ChangeInfoFormType,
  watch: UseFormWatch<ChangeInfoFormType>,
) => {
  let result = false
  const keys = Object.keys(defaultValues) as Array<keyof ChangeInfoFormType>

  keys.forEach((key) => {
    const currentItem = defaultValues[key]

    if (Array.isArray(currentItem)) {
      currentItem.forEach((item, index) => {
        // @ts-ignore
        if (item !== watch(key)?.[index]) {
          result = true
        }
      })
    } else if (typeof currentItem === 'object' && !!currentItem) {
      //  @ts-ignore
      if (currentItem?.key !== watch(key)?.key) {
        result = true
      }
    } else {
      if (currentItem !== watch(key)) {
        result = true
      }
    }
  })

  return result
}

export default observer(EditFullCustomerInfo)
